import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import type {
  SurveyInstrumentDto, SurveyQuestionDto, SurveyVocabularyDto,
} from '@/shared/api/types'
import { addQuestion, updateQuestion } from './api'
import { Pill, count, questionTypeLabel } from './vocab'
import { LinkedEntitySelect, useSurveyLinkChoices } from './QuestionLinkPicker'

/**
 * One question, in full — and the pieces every instrument dialog shares.
 *
 * ## Why the shared refusal helpers live in this file and not in `BuilderPage`
 *
 * `BuilderPage` imports all five dialogs. A dialog importing back from it would
 * close the loop, and a cycle that happens to work under Vite today is a bug
 * waiting for someone to move a call to module scope. This is the dialog the
 * instrument is actually built out of, so the shared refusal — the one every
 * write to a published version gets — is defined here and imported downwards.
 */

/**
 * The 409 that is not an error.
 *
 * `survey/version-published` is the server telling the client what to OFFER, not
 * what went wrong: version N is frozen because interviews already point at it,
 * and the way to make this edit is version N+1. Rendering it as a red strip
 * would be the client repeating "no" where the server said "here instead".
 *
 * Returns the server's own sentence, because it names the version numbers and
 * says what happens to the answers already collected — three facts a generic
 * "this version is published" would drop.
 */
export function publishedRefusal(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null
  return e.code === 'version-published' ? explain(e) : null
}

/** Everything a dialog needs to render the offer without knowing how versions work. */
export interface VersionOffer {
  /** The version a refusal would send the author to. */
  nextVersionNo: number
  canUpdate: boolean
  busy: boolean
  /** Creates version N+1 and switches the page to it. */
  onCreate: () => void
}

/**
 * The refusal and the way out, in one block.
 *
 * Deliberately `Note` and not `ErrorStrip`: nothing has gone wrong, and the
 * difference between "I cannot edit this" and "I do not know why nothing
 * happens" is that this says which and hands over the button.
 */
export function PublishedOffer({ sentence, offer }: { sentence: string; offer: VersionOffer }) {
  return (
    <Note tone="info">
      {sentence}
      {offer.canUpdate && (
        <button type="button" className="erp-btn erp-btn-ghost h-8 ml-2 text-erp-sm text-chocolate"
          disabled={offer.busy} onClick={offer.onCreate}>
          Create version {offer.nextVersionNo}
        </button>
      )}
    </Note>
  )
}

/**
 * Shown once the page has moved to the fresh version under an open dialog.
 *
 * A copy-on-write version re-keys everything in it, so a dialog that was editing
 * a row by id is now holding an id that exists nowhere. An add can simply be
 * saved again; an edit has to be reopened, and saying so beats a 404.
 */
export function RetargetedNote({ versionNo, keyedToARow }: {
  versionNo: number
  /** True for any dialog addressing an existing question, option or rule by id. */
  keyedToARow: boolean
}) {
  return (
    <Note tone="info">
      {keyedToARow
        ? `Version ${versionNo} is a fresh copy, so every question, option and branch in it has `
          + 'a new id — including the one this dialog was pointed at. Close this and reopen it '
          + `from the outline of version ${versionNo}; nothing has been lost.`
        : `Now writing into version ${versionNo}. Nothing you typed was lost — press Save again.`}
    </Note>
  )
}

// ── option kinds and anchors ────────────────────────────────────────────────

/**
 * What each code MEANS. Not what codes exist.
 *
 * The list itself comes from `getVocabulary().optionKind` and nothing here adds
 * to it or filters it — a kind this build has never seen still appears in the
 * select, as its raw code, with no sentence beside it. That is rule 4 holding:
 * the hand-written half is the prose, and prose cannot drift into offering a
 * value the server will not accept.
 *
 * `vocab.tsx` has no label map for these, so the select shows the raw code. The
 * meaning goes next to it as description, which is what it is.
 */
const OPTION_KIND_MEANING: Record<string, string> = {
  SUBSTANTIVE: 'A real answer to the question that was asked.',
  OTHER_SPECIFY: 'A real answer plus free text the interviewer writes down. The text is coded '
    + 'afterwards on Verbatims; until then it is not one of the substantive categories.',
  DONT_KNOW: 'The respondent has no view. Not the same as declining, and not the same as being '
    + 'asked and saying nothing.',
  REFUSED: 'The respondent declined to answer. Non-response, not an opinion.',
  NOT_APPLICABLE: 'The question does not apply to this respondent.',
}

const OPTION_ANCHOR_MEANING: Record<string, string> = {
  NONE: 'Takes part in order randomisation like any other option.',
  TOP: 'Pinned to the top of the card. Randomisation moves everything around it and leaves it '
    + 'where it is.',
  BOTTOM: 'Pinned to the bottom of the card — where "Don\'t know" and "Refused" belong, so a '
    + 'respondent meets the real answers first.',
}

/** The kind whose answers are in the base of a percentage computed on the substantive base. */
const SUBSTANTIVE = 'SUBSTANTIVE'

interface OptionDraft {
  /** Local only — a server id would be a lie for a row that has never been saved. */
  key: string
  /** The server id, kept so an edit updates a row rather than replacing it. */
  id: string | null
  code: string
  label: string
  kindCode: string
  exclusive: boolean
  anchorCode: string
  score: string
  /**
   * What this option is ABOUT — a candidate, a party, a programme.
   *
   * Edited here now. The comment this replaces said the link "is chosen in the
   * Builder, two panels away"; it was chosen nowhere, and an option authored
   * through this dialog linked to nobody. See `QuestionLinkPicker.tsx` for what
   * that silently cost.
   */
  linkedEntityType: string | null
  linkedEntityRef: string | null
}

let draftSeq = 0
const newOption = (kindCode: string, anchorCode: string): OptionDraft => ({
  key: `opt-${draftSeq += 1}`, id: null, code: '', label: '',
  kindCode, exclusive: false, anchorCode, score: '',
  linkedEntityType: null, linkedEntityRef: null,
})

/** `''` is "not declared"; `Number('')` is 0, and 0 is a different statement. */
const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v))

const TYPES_WITH_OPTIONS = ['SINGLE_CHOICE', 'MULTI_CHOICE', 'RANKING']
const TYPES_WITH_SCALE = ['RATING_SCALE', 'LIKERT', 'NPS']

export function QuestionDialog({
  open, versionId, pollId, question, blocks, questionCount, vocabulary, offer, onClose,
  onSaved,
}: {
  open: boolean
  versionId: string
  /**
   * The poll this version belongs to, for the cross-module picker.
   *
   * Null only if a caller has none to give, in which case the picker is not
   * offered — never an unfiltered list of every candidate in the deployment,
   * which is the shape this product exists to avoid.
   */
  pollId: string | null
  /** `null` is "add". */
  question: SurveyQuestionDto | null
  blocks: SurveyInstrumentDto['blocks']
  /** For the position hint — where an appended question would land. */
  questionCount: number
  /** `getVocabulary()`, or null while it loads. Every picker here is built from it. */
  vocabulary: SurveyVocabularyDto | null
  offer: VersionOffer
  onClose: () => void
  onSaved: (instrument: SurveyInstrumentDto) => void
}) {
  const [code, setCode] = useState('')
  const [text, setText] = useState('')
  const [helpText, setHelpText] = useState('')
  const [typeCode, setTypeCode] = useState('')
  const [required, setRequired] = useState(true)
  const [blockId, setBlockId] = useState('')
  const [position, setPosition] = useState('')
  const [randomiseOptions, setRandomiseOptions] = useState(false)
  const [scaleMin, setScaleMin] = useState('')
  const [scaleMax, setScaleMax] = useState('')
  const [numericMin, setNumericMin] = useState('')
  const [numericMax, setNumericMax] = useState('')
  const [minSelections, setMinSelections] = useState('')
  const [maxSelections, setMaxSelections] = useState('')
  const [options, setOptions] = useState<OptionDraft[]>([])
  /** What the QUESTION is about. Options carry their own, on the draft rows. */
  const [linked, setLinked] = useState<{ typeCode: string | null; ref: string | null }>(
    { typeCode: null, ref: null })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The version the refusal was about, so a page that has moved on can be detected. */
  const [refusal, setRefusal] = useState<{ sentence: string; versionId: string } | null>(null)

  const kinds = vocabulary?.optionKind ?? []
  const anchors = vocabulary?.optionAnchor ?? []
  const defaultKind = kinds.includes(SUBSTANTIVE) ? SUBSTANTIVE : (kinds[0] ?? SUBSTANTIVE)
  const defaultAnchor = anchors.includes('NONE') ? 'NONE' : (anchors[0] ?? 'NONE')

  useEffect(() => {
    if (!open) return
    setCode(question?.code ?? '')
    setText(question?.text ?? '')
    setHelpText(question?.helpText ?? '')
    setTypeCode(question?.typeCode ?? '')
    setRequired(question?.required ?? true)
    setBlockId(question?.blockId ?? '')
    setPosition(question ? String(question.position) : '')
    setRandomiseOptions(question?.randomiseOptions ?? false)
    setScaleMin(question?.scaleMin === null || question?.scaleMin === undefined
      ? '' : String(question.scaleMin))
    setScaleMax(question?.scaleMax === null || question?.scaleMax === undefined
      ? '' : String(question.scaleMax))
    setNumericMin(question?.numericMin === null || question?.numericMin === undefined
      ? '' : String(question.numericMin))
    setNumericMax(question?.numericMax === null || question?.numericMax === undefined
      ? '' : String(question.numericMax))
    setMinSelections(question?.minSelections === null || question?.minSelections === undefined
      ? '' : String(question.minSelections))
    setMaxSelections(question?.maxSelections === null || question?.maxSelections === undefined
      ? '' : String(question.maxSelections))
    setOptions((question?.options ?? []).map((o) => ({
      key: `opt-${draftSeq += 1}`, id: o.id, code: o.code, label: o.label,
      kindCode: o.kindCode, exclusive: o.exclusive, anchorCode: o.anchorCode,
      score: o.score === null || o.score === undefined ? '' : String(o.score),
      linkedEntityType: o.linkedEntity?.typeCode ?? null,
      linkedEntityRef: o.linkedEntity?.ref ?? null,
    })))
    setLinked({
      typeCode: question?.linkedEntity?.typeCode ?? null,
      ref: question?.linkedEntity?.ref ?? null,
    })
    setError(null)
    setRefusal(null)
  }, [open, question])

  // Only while the dialog is open: a closed dialog fetching a side list on every
  // render of the Builder is a request per keystroke somewhere else.
  const links = useSurveyLinkChoices(pollId, open)
  /**
   * The column appears only when there is something to put in it.
   *
   * A poll that declares nothing gets a narrower table rather than a column of
   * empty selects — and the absence is explained once, above, next to the
   * question-level picker, instead of eight times down a column.
   */
  const showOptionLinks = links.choices.length > 0
  /** Eight fixed columns, plus the link one when it is shown. */
  const optionColumns = showOptionLinks ? 9 : 8

  const usesOptions = TYPES_WITH_OPTIONS.includes(typeCode)
  const usesScale = TYPES_WITH_SCALE.includes(typeCode)
  const usesNumeric = typeCode === 'NUMBER'
  const usesSelections = typeCode === 'MULTI_CHOICE'

  const substantiveCount = useMemo(
    () => options.filter((o) => o.kindCode === SUBSTANTIVE).length, [options])
  const nonSubstantive = useMemo(
    () => options.filter((o) => o.kindCode !== SUBSTANTIVE), [options])
  /** Which kinds are actually on this card — the legend explains those and no others. */
  const kindsInUse = useMemo(
    () => [...new Set(options.map((o) => o.kindCode))], [options])
  const anchoredCount = options.filter((o) => o.anchorCode && o.anchorCode !== 'NONE').length

  const setOption = (key: string, patch: Partial<OptionDraft>) =>
    setOptions((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const move = (index: number, by: -1 | 1) => setOptions((rows) => {
    const to = index + by
    if (to < 0 || to >= rows.length) return rows
    const next = [...rows]
    const a = next[index]
    const b = next[to]
    if (!a || !b) return rows
    next[index] = b
    next[to] = a
    return next
  })

  /** The page switched versions under us — see `RetargetedNote`. */
  const movedOn = refusal !== null && refusal.versionId !== versionId

  async function submit() {
    setBusy(true)
    setError(null)
    setRefusal(null)
    try {
      const body: Record<string, unknown> = {
        code: code.trim(),
        text: text.trim(),
        helpText: helpText.trim() || null,
        typeCode,
        required,
        // Empty means "append and let the server number it". A question's
        // position is renumbered on every add and delete, so a client that
        // insisted on one would be fighting the document it just received.
        position: numOrNull(position),
        blockId: blockId || null,
        randomiseOptions,
        // The type decides which of these mean anything, and the ones that do
        // not are sent as null rather than left behind. A NUMBER question that
        // kept a scaleMax from when it was a LIKERT would constrain capture in
        // a way nothing on this form explains.
        scaleMin: usesScale ? numOrNull(scaleMin) : null,
        scaleMax: usesScale ? numOrNull(scaleMax) : null,
        numericMin: usesNumeric ? numOrNull(numericMin) : null,
        numericMax: usesNumeric ? numOrNull(numericMax) : null,
        minSelections: usesSelections ? numOrNull(minSelections) : null,
        maxSelections: usesSelections ? numOrNull(maxSelections) : null,
        linkedEntityType: linked.typeCode,
        linkedEntityRef: linked.ref,
        // NOT edited on this form, and on `QuestionRequest`. A PUT replaces the
        // whole record, so omitting it would detach every MATRIX row from its
        // parent and the capture screen would render the rows as unrelated
        // questions.
        parentQuestionId: question?.parentQuestionId ?? null,
        options: usesOptions
          ? options.map((o, i) => ({
              code: o.code.trim(),
              label: o.label.trim(),
              position: i + 1,
              kindCode: o.kindCode,
              exclusive: o.exclusive,
              anchorCode: o.anchorCode,
              score: numOrNull(o.score),
              linkedEntityType: o.linkedEntityType,
              linkedEntityRef: o.linkedEntityRef,
            }))
          : [],
      }
      const instrument = question
        ? await updateQuestion(question.id, body)
        : await addQuestion(versionId, body)
      onSaved(instrument)
    } catch (e) {
      // `survey/question-code-taken` lands here and is rendered as itself: the
      // code is what an answer joins on, so a collision is the author's to fix
      // and the server's sentence says why it matters.
      const frozen = publishedRefusal(e)
      if (frozen) setRefusal({ sentence: frozen, versionId })
      else setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={question ? `Question ${question.code}` : 'New question'}
      description="Code, wording, type, and the fields that only some types have. The code is what every export column and every answer joins on."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Never gated on a client-side validity check, and never on whether the
            version is published. Both refusals are the server's, and both come
            back with a sentence a greyed-out button cannot say.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : question ? 'Save question' : 'Add question'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {refusal && !movedOn && <PublishedOffer sentence={refusal.sentence} offer={offer} />}
        {movedOn && <RetargetedNote versionNo={offer.nextVersionNo} keyedToARow={!!question} />}

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Field label="Code" required
              hint="At most 16 characters. It becomes a column name in every export.">
              <input className="erp-input h-9 w-full font-mono text-erp-sm" value={code}
                maxLength={16} placeholder="Q1"
                onChange={(e) => setCode(e.target.value)} />
            </Field>
          </div>
          <div className="col-span-8">
            <Field label="Type" required
              hint={vocabulary
                ? 'From the server\'s own list. Changing it clears the fields the new type has no use for.'
                : 'Loading the server\'s question types…'}>
              <select className="erp-input h-9 w-full" value={typeCode}
                onChange={(e) => setTypeCode(e.target.value)}>
                <option value="">Choose a type…</option>
                {(vocabulary?.questionType ?? []).map((t) => (
                  <option key={t} value={t}>{questionTypeLabel(t)}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <Field label="Question text" required hint="The words the interviewer reads out.">
          <textarea className="erp-input w-full min-h-[64px] py-2" value={text} maxLength={4000}
            onChange={(e) => setText(e.target.value)}
            placeholder="If the election were held tomorrow, who would you vote for?" />
        </Field>

        <Field label="Help text"
          hint="Shown to the interviewer, not read out. Probes, clarifications, what counts as which answer.">
          <textarea className="erp-input w-full min-h-[48px] py-2" value={helpText} maxLength={4000}
            onChange={(e) => setHelpText(e.target.value)}
            placeholder="Do not read the list. Code the first name mentioned." />
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Block" hint="Groups this question in the outline and gives a branch something to act on wholesale.">
              <select className="erp-input h-9 w-full" value={blockId}
                onChange={(e) => setBlockId(e.target.value)}>
                <option value="">No block</option>
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>{b.code}{b.title ? ` · ${b.title}` : ''}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Position"
              hint={question ? 'Currently ' + question.position : `Empty appends at ${questionCount + 1}`}>
              <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={1}
                value={position} onChange={(e) => setPosition(e.target.value)} placeholder="" />
            </Field>
          </div>
          <div className="col-span-4 flex flex-col justify-center gap-1.5 pt-4">
            <label className="flex items-center gap-2 text-erp-sm">
              <input type="checkbox" checked={required}
                onChange={(e) => setRequired(e.target.checked)} />
              Required
            </label>
            <label className="flex items-center gap-2 text-erp-sm">
              <input type="checkbox" checked={randomiseOptions}
                onChange={(e) => setRandomiseOptions(e.target.checked)} />
              Randomise option order
            </label>
          </div>
        </div>

        {pollId && (
          <Field label="This question is about"
            hint={links.empty
              ? undefined
              : 'Chosen from what this poll declares it measures. Add another on the '
                + 'configurator’s cross-module step.'}>
            {links.empty ? (
              <Note>
                This poll has not declared what it is about yet, so there is nothing to link a
                question to. The configurator’s <strong>Cross-module links</strong> step is
                where candidates, parties and programmes are declared — it comes before this one
                for exactly this reason.
              </Note>
            ) : (
              <LinkedEntitySelect value={linked} onChange={setLinked} links={links}
                ariaLabel="What this question is about" />
            )}
          </Field>
        )}

        {linked.ref && (
          <p className="text-erp-xs text-muted-foreground -mt-1">
            A linked question is findable from that record’s own screen. For a weighted SHARE to
            reach a candidate, the link has to be on the OPTION that names them — that is what
            the weight run pushes.
          </p>
        )}

        {required && (
          <p className="text-erp-xs text-muted-foreground -mt-1">
            Required means capture refuses to move on without an answer — which is why a
            question a respondent may genuinely have no answer to needs a Don&apos;t know option
            rather than being left optional. An optional question and an unanswered required one
            are indistinguishable in the dataset.
          </p>
        )}

        {usesScale && (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Field label="Scale min" hint="Lowest point on the scale.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  value={scaleMin} onChange={(e) => setScaleMin(e.target.value)} placeholder="0" />
              </Field>
            </div>
            <div className="col-span-3">
              <Field label="Scale max" hint="Highest point.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  value={scaleMax} onChange={(e) => setScaleMax(e.target.value)} placeholder="10" />
              </Field>
            </div>
            <div className="col-span-6 self-center pt-3">
              <p className="text-erp-xs text-muted-foreground">
                An NPS is conventionally 0–10 and a Likert 1–5. The bounds are stored with the
                question, so a wave that changed them is visible rather than being a break in the
                trend nobody can explain.
              </p>
            </div>
          </div>
        )}

        {usesNumeric && (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Field label="Minimum" hint="Refused below this at capture.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  value={numericMin} onChange={(e) => setNumericMin(e.target.value)}
                  placeholder="18" />
              </Field>
            </div>
            <div className="col-span-3">
              <Field label="Maximum" hint="Refused above this.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  value={numericMax} onChange={(e) => setNumericMax(e.target.value)}
                  placeholder="120" />
              </Field>
            </div>
            <div className="col-span-6 self-center pt-3">
              <p className="text-erp-xs text-muted-foreground">
                Bounds catch a mistyped age at the door. Leave them empty and a 191-year-old
                respondent is caught on Quality, weeks later, if at all.
              </p>
            </div>
          </div>
        )}

        {usesSelections && (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Field label="Min selections" hint="0 lets a respondent pick nothing.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  min={0} value={minSelections}
                  onChange={(e) => setMinSelections(e.target.value)} placeholder="1" />
              </Field>
            </div>
            <div className="col-span-3">
              <Field label="Max selections" hint="Empty means as many as they like.">
                <input className="erp-input h-9 w-full text-right tabular-nums" type="number"
                  min={1} value={maxSelections}
                  onChange={(e) => setMaxSelections(e.target.value)} placeholder="3" />
              </Field>
            </div>
            <div className="col-span-6 self-center pt-3">
              <p className="text-erp-xs text-muted-foreground">
                A multi-choice question has no single base: the shares are of respondents, not of
                mentions, and they do not add to 100%. The cap is what makes that readable — "up
                to three" is a sentence a report can print.
              </p>
            </div>
          </div>
        )}

        {usesOptions && (
          <div className="pt-1">
            <div className="flex items-center justify-between gap-2 pb-2">
              <div className="min-w-0">
                <p className="text-erp-sm font-medium">Options</p>
                <p className="text-erp-xs text-muted-foreground">
                  What is on the card, in the order it is read. The kind decides whether an answer
                  is an opinion or the absence of one.
                </p>
              </div>
              <button type="button" className="erp-btn h-8 text-erp-xs"
                onClick={() => setOptions((r) => [...r, newOption(defaultKind, defaultAnchor)])}>
                <Plus className="h-3 w-3" /> Add option
              </button>
            </div>

            <ErpTable head={(
              <>
                {/* Explicit widths: left to auto layout the two selects collapse
                    until OTHER_SPECIFY reads as OTHER_SPE… , and the kind is the
                    column somebody is here to check. */}
                <Th className="w-16">Order</Th>
                <Th className="w-28">Code</Th>
                <Th>Label</Th>
                <Th className="w-40">Kind</Th>
                <Th className="w-28">Anchor</Th>
                {showOptionLinks && <Th className="w-48">Is about</Th>}
                <Th className="w-16 text-center">Excl.</Th>
                <Th className="w-20 text-right">Score</Th>
                <Th />
              </>
            )}>
              {options.length === 0 ? (
                <tr>
                  <td colSpan={optionColumns}
                    className="px-4 py-8 text-center text-erp-sm text-muted-foreground">
                    No options. A {questionTypeLabel(typeCode)} question with none cannot be
                    answered, and the server refuses it.
                  </td>
                </tr>
              ) : options.map((o, i) => (
                <tr key={o.key} className="hover:bg-muted/40 align-top">
                  <Td>
                    <span className="inline-flex items-center gap-0.5">
                      <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                        disabled={i === 0} aria-label={`Move ${o.code || 'option'} up`}
                        onClick={() => move(i, -1)}>
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                        disabled={i === options.length - 1}
                        aria-label={`Move ${o.code || 'option'} down`}
                        onClick={() => move(i, 1)}>
                        <ArrowDown className="h-3 w-3" />
                      </button>
                    </span>
                  </Td>
                  <Td>
                    <input className="erp-input h-8 w-full font-mono text-erp-xs" maxLength={32}
                      value={o.code} placeholder="CODE"
                      onChange={(e) => setOption(o.key, { code: e.target.value })} />
                  </Td>
                  <Td>
                    <input className="erp-input h-8 w-full text-erp-sm" maxLength={300}
                      value={o.label} placeholder="What the respondent hears"
                      onChange={(e) => setOption(o.key, { label: e.target.value })} />
                  </Td>
                  <Td>
                    <select className="erp-input h-8 w-full text-erp-xs font-mono"
                      value={o.kindCode}
                      onChange={(e) => setOption(o.key, { kindCode: e.target.value })}>
                      {/* Raw codes: `vocab.tsx` has no label map for option kinds,
                          and inventing one here would be a second code list. The
                          meanings are in the legend under the table. */}
                      {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                      {!kinds.includes(o.kindCode) && (
                        <option value={o.kindCode}>{o.kindCode}</option>
                      )}
                    </select>
                  </Td>
                  <Td>
                    <select className="erp-input h-8 w-full text-erp-xs font-mono"
                      value={o.anchorCode}
                      onChange={(e) => setOption(o.key, { anchorCode: e.target.value })}>
                      {anchors.map((a) => <option key={a} value={a}>{a}</option>)}
                      {!anchors.includes(o.anchorCode) && (
                        <option value={o.anchorCode}>{o.anchorCode}</option>
                      )}
                    </select>
                  </Td>
                  {showOptionLinks && (
                    <Td>
                      {/*
                        THE LINK THAT MOVES A NUMBER. `pushCandidateOverlays` reads
                        the OPTION's link, not the question's: the weighted share
                        it pushes onto a candidate's record is the share of the
                        option that names them.
                      */}
                      <LinkedEntitySelect compact links={links}
                        ariaLabel={`What option ${o.code || '(new)'} is about`}
                        value={{ typeCode: o.linkedEntityType, ref: o.linkedEntityRef }}
                        onChange={(next) => setOption(o.key, {
                          linkedEntityType: next.typeCode, linkedEntityRef: next.ref,
                        })} />
                    </Td>
                  )}
                  <Td className="text-center">
                    <input type="checkbox" checked={o.exclusive}
                      aria-label={`${o.code || 'Option'} is exclusive`}
                      onChange={(e) => setOption(o.key, { exclusive: e.target.checked })} />
                  </Td>
                  <Td className="text-right">
                    <input className="erp-input h-8 w-full text-right tabular-nums text-erp-xs"
                      type="number" step="any" value={o.score} placeholder="—"
                      onChange={(e) => setOption(o.key, { score: e.target.value })} />
                  </Td>
                  <Td className="text-right">
                    <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                      aria-label={`Remove option ${o.code || '(new)'}`}
                      onClick={() => setOptions((rows) => rows.filter((r) => r.key !== o.key))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </Td>
                </tr>
              ))}
            </ErpTable>

            <div className="pt-3 space-y-2">
              <BaseStatement substantive={substantiveCount} total={options.length}
                nonSubstantive={nonSubstantive.map((o) => o.code || o.label || '(unnamed)')} />

              {kindsInUse.length > 0 && (
                <ul className="space-y-1">
                  {kindsInUse.map((k) => (
                    <li key={k} className="text-erp-xs text-muted-foreground flex gap-2">
                      <span className="font-mono shrink-0 text-foreground/70">{k}</span>
                      <span className="min-w-0">
                        {OPTION_KIND_MEANING[k]
                          ?? 'This build has no description for this kind. It is the server\'s '
                            + 'code and it is stored as sent.'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="text-erp-xs text-muted-foreground">
                <span className="font-medium">Exclusive</span> means picking it clears everything
                else — "None of these" alongside a multi-choice list.{' '}
                <span className="font-medium">Score</span> is the numeric value this answer
                contributes where the question is scored; it is not a weight and it is not a
                position.
              </p>

              {anchoredCount > 0 ? (
                <p className="text-erp-xs text-muted-foreground">
                  {anchoredCount} option{anchoredCount === 1 ? ' is' : 's are'} anchored, so
                  randomisation leaves {anchoredCount === 1 ? 'it' : 'them'} where they are.
                  {' '}{options.filter((o) => o.anchorCode && o.anchorCode !== 'NONE')
                    .map((o) => `${o.code || o.label}: ${OPTION_ANCHOR_MEANING[o.anchorCode]
                      ?? o.anchorCode}`).join(' ')}
                </p>
              ) : randomiseOptions && (
                <p className="text-erp-xs text-muted-foreground">
                  Nothing is anchored and this question randomises, so every option moves —
                  including Don&apos;t know and Refused, which is the one place respondents expect
                  to find in the same place every time. Anchor them BOTTOM.
                </p>
              )}
            </div>
          </div>
        )}

        {!usesOptions && typeCode !== '' && (
          <Note tone="info">
            {questionTypeLabel(typeCode)} answers are not chosen from a card, so there are no
            options to declare and no option kinds to set. The branch preview on the builder can
            only follow option answers, so a rule reading this question&apos;s value is evaluated
            by the server and shows as never firing in the preview.
          </Note>
        )}
      </div>
    </Modal>
  )
}

/**
 * How many of these options are an opinion, and what that does to a percentage.
 *
 * The single most important thing on this form. PollSphere counted "Don't know"
 * and "Refuse to answer" as ordinary options and divided by them, so every share
 * it published was smaller than the share of people who actually held the view —
 * by however many people had no view. The defect is invisible in the output: the
 * numbers look like numbers.
 */
function BaseStatement({ substantive, total, nonSubstantive }: {
  substantive: number
  total: number
  nonSubstantive: string[]
}) {
  if (total === 0) return null
  return (
    <div className="rounded border border-border bg-muted/20 p-2.5 space-y-1">
      <p className="text-erp-sm">
        <Pill>{count(substantive)} of {count(total)} substantive</Pill>
      </p>
      <p className="text-erp-xs text-muted-foreground">
        {substantive === total
          ? 'Every option here is a substantive answer, so the substantive base and the answered '
            + 'base are the same number. If this question can genuinely be met with "I don\'t '
            + 'know", add that option rather than letting it arrive as a refusal to continue.'
          : `A share computed on the substantive base divides by the ${substantive} people who `
            + `picked one of the substantive options. ${nonSubstantive.join(', ')} `
            + `${nonSubstantive.length === 1 ? 'is' : 'are'} reported as ${
              nonSubstantive.length === 1 ? 'its own row' : 'their own rows'}, not folded into `
            + 'the denominator — which is the difference between "42% of those with a view" and '
            + '"42% of everyone asked".'}
      </p>
    </div>
  )
}
